import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  ClipboardList,
  Shield,
  MessageCircle,
  Settings,
  GraduationCap,
  FileText,
  Brain,
  Crown,
  Search,
  ChevronLeft,
  ChevronRight,
  Presentation,
  HelpCircle,
  Menu,
  X,
  Gamepad2 } from
'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'motion/react';
import { useIsMobile } from '@/hooks/use-mobile';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeTab, setActiveTab, isCollapsed, setIsCollapsed }) => {
  const { isSuperAdmin, isAdmin, userTier, hasAIAccess } = useAuth();
  const isMobile = useIsMobile();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Enhanced debug logging
  console.log('Sidebar - isSuperAdmin:', isSuperAdmin);
  console.log('Sidebar - isAdmin:', isAdmin);
  console.log('Sidebar - userTier:', userTier);
  console.log('Sidebar - hasAIAccess basic:', hasAIAccess('basic'));
  console.log('Sidebar - hasAIAccess advanced:', hasAIAccess('advanced'));

  const superAdminMenuItems = [
  { id: 'superadmin', label: 'Super Admin', icon: Shield, badge: 'ADMIN', requiresAccess: null },
  { id: 'teachers', label: 'Manage Teachers', icon: Users, requiresAccess: null },
  { id: 'schools', label: 'Schools', icon: GraduationCap, requiresAccess: null },
  { id: 'settings', label: 'System Settings', icon: Settings, requiresAccess: null }];


  const teacherMenuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, requiresAccess: null },
  { id: 'classes', label: 'My Classes', icon: BookOpen, requiresAccess: null },
  { id: 'questions-pool', label: 'Questions Pool', icon: HelpCircle, requiresAccess: null },
  { id: 'gameify', label: 'Game-ify (beta)', icon: Gamepad2, badge: 'NEW', requiresAccess: null },
  { id: 'lesson-plans', label: 'Lesson Plans', icon: FileText, badge: 'AI', requiresAccess: 'advanced' as const },
  { id: 'presentation-generator', label: 'Slides Generator (beta)', icon: Presentation, badge: 'AI', requiresAccess: 'advanced' as const },
  { id: 'quizzes', label: 'Quiz Generator', icon: ClipboardList, badge: 'AI', requiresAccess: 'advanced' as const },
  { id: 'plagiarism', label: 'Plag Checker (beta)', icon: Search, badge: 'AI', requiresAccess: 'advanced' as const },
  { id: 'chat', label: 'AI Assistant', icon: Brain, badge: 'AI', requiresAccess: 'basic' as const }];


  const menuItems = isSuperAdmin ? [...superAdminMenuItems, ...teacherMenuItems] : teacherMenuItems;

  console.log('Sidebar - menuItems:', menuItems.map((item) => ({ id: item.id, label: item.label, requiresAccess: item.requiresAccess })));

  const handleMobileMenuItemClick = (itemId: string) => {
    console.log('Sidebar - Mobile menu item clicked:', itemId);
    setActiveTab(itemId);
    setIsMobileMenuOpen(false);
  };

  const handleDesktopMenuItemClick = (itemId: string) => {
    console.log('Sidebar - Desktop menu item clicked:', itemId);
    setActiveTab(itemId);
  };

  // Mobile Floating Action Button Component
  const MobileFloatingMenu = () =>
  <>
      {/* Floating Action Button */}
      <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3, delay: 0.5 }}
      className="fixed bottom-6 left-6 z-50" data-id="kjdcrpmus" data-path="src/components/layout/Sidebar.tsx">

        <Button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className={`
            w-14 h-14 rounded-full shadow-lg border-2 border-white
            bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700
            text-white transition-all duration-300 transform hover:scale-110
            ${isMobileMenuOpen ? 'rotate-45' : 'rotate-0'}
          `} data-id="uhp2jge0t" data-path="src/components/layout/Sidebar.tsx">

          {isMobileMenuOpen ?
        <X className="h-6 w-6" data-id="glsmntpis" data-path="src/components/layout/Sidebar.tsx" /> :

        <Menu className="h-6 w-6" data-id="3upub5dv9" data-path="src/components/layout/Sidebar.tsx" />
        }
        </Button>
      </motion.div>

      {/* Expanded Menu Overlay */}
      <AnimatePresence data-id="3owzdp45i" data-path="src/components/layout/Sidebar.tsx">
        {isMobileMenuOpen &&
      <>
            {/* Backdrop */}
            <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setIsMobileMenuOpen(false)} data-id="ouf1c1g39" data-path="src/components/layout/Sidebar.tsx" />


            {/* Menu Panel */}
            <motion.div
          initial={{ x: -300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -300, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 w-80 max-w-[90vw] h-[80vh] bg-white/95 backdrop-blur-md rounded-tr-3xl shadow-2xl border-t border-r border-gray-200 z-50" data-id="lgx017gq4" data-path="src/components/layout/Sidebar.tsx">

              <div className="p-6 h-full flex flex-col" data-id="jbexflrj0" data-path="src/components/layout/Sidebar.tsx">
                {/* Header */}
                <div className="mb-6" data-id="j4v18zbqs" data-path="src/components/layout/Sidebar.tsx">
                  <h2 className="text-xl font-bold text-gray-800 mb-2" data-id="b5x65soq9" data-path="src/components/layout/Sidebar.tsx">Menu</h2>
                  {!isSuperAdmin && userTier &&
              <div className={`p-3 rounded-lg border ${
              userTier.tier_level === 2 ?
              'bg-purple-50 border-purple-200' :
              'bg-green-50 border-green-200'}`
              } data-id="b9a9c212l" data-path="src/components/layout/Sidebar.tsx">
                      <div className="flex items-center gap-2" data-id="bp386f1yo" data-path="src/components/layout/Sidebar.tsx">
                        {userTier.tier_level === 2 ?
                  <Crown className="w-4 h-4 text-purple-600" data-id="anbcrckzm" data-path="src/components/layout/Sidebar.tsx" /> :

                  <Shield className="w-4 h-4 text-green-600" data-id="nsf0dym6d" data-path="src/components/layout/Sidebar.tsx" />
                  }
                        <span className={`text-sm font-medium ${
                  userTier.tier_level === 2 ? 'text-purple-700' : 'text-green-700'}`
                  } data-id="yrmk5l1nd" data-path="src/components/layout/Sidebar.tsx">
                          {userTier.tier_name} Tier
                        </span>
                      </div>
                      <p className={`text-xs mt-1 ${
                userTier.tier_level === 2 ? 'text-purple-600' : 'text-green-600'}`
                } data-id="acc6vjtir" data-path="src/components/layout/Sidebar.tsx">
                        {userTier.tier_level === 2 ?
                  'Full AI access enabled' :
                  'Basic AI access only'
                  }
                      </p>
                    </div>
              }
                  
                  {/* Admin Badge */}
                  {isAdmin && !isSuperAdmin &&
              <div className="mt-2 p-2 rounded-lg bg-blue-50 border border-blue-200" data-id="jyyq634dy" data-path="src/components/layout/Sidebar.tsx">
                      <div className="flex items-center gap-2" data-id="3in9wa3x5" data-path="src/components/layout/Sidebar.tsx">
                        <Shield className="w-4 h-4 text-blue-600" data-id="l2spk3atv" data-path="src/components/layout/Sidebar.tsx" />
                        <span className="text-sm font-medium text-blue-700" data-id="98e2tqbdw" data-path="src/components/layout/Sidebar.tsx">Admin Access</span>
                      </div>
                    </div>
              }
                </div>

                {/* Menu Items */}
                <ScrollArea className="flex-1" data-id="9lhoiz37q" data-path="src/components/layout/Sidebar.tsx">
                  <div className="space-y-2" data-id="ht4fn6w7u" data-path="src/components/layout/Sidebar.tsx">
                    {menuItems.map((item, index) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  // Admin users should have access to all features, including Questions Pool
                  const hasAccess = !item.requiresAccess || hasAIAccess(item.requiresAccess) || isSuperAdmin || isAdmin;

                  console.log(`Sidebar - Mobile item ${item.id}: hasAccess=${hasAccess}, requiresAccess=${item.requiresAccess}, isSuperAdmin=${isSuperAdmin}, isAdmin=${isAdmin}`);

                  return (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }} data-id="yf2t5n75q" data-path="src/components/layout/Sidebar.tsx">

                          <Button
                        variant={isActive ? 'default' : 'ghost'}
                        className={`w-full justify-start text-left space-x-3 h-12 ${
                        isActive ?
                        'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md' :
                        hasAccess ?
                        'hover:bg-gray-100' :
                        'text-gray-400 cursor-not-allowed hover:bg-transparent'}`
                        }
                        onClick={() => hasAccess && handleMobileMenuItemClick(item.id)}
                        disabled={!hasAccess} data-id="vz4lx2gya" data-path="src/components/layout/Sidebar.tsx">

                            <Icon className="h-5 w-5" data-id="erzbwt33j" data-path="src/components/layout/Sidebar.tsx" />
                            <span className="flex-1 font-medium" data-id="9lq94fxjx" data-path="src/components/layout/Sidebar.tsx">{item.label}</span>
                            
                            {/* Access restriction indicator */}
                            {!hasAccess && item.requiresAccess &&
                        <Badge
                          variant="outline"
                          className="text-xs bg-gray-100 text-gray-500 border-gray-300" data-id="i3q1s18oe" data-path="src/components/layout/Sidebar.tsx">

                                Tier 2
                              </Badge>
                        }
                            
                            {/* Regular badges */}
                            {item.badge && hasAccess &&
                        <Badge
                          variant={item.badge === 'AI' ? 'secondary' : 'default'}
                          className={`text-xs ${
                          item.badge === 'ADMIN' ?
                          'bg-red-500 text-white' :
                          item.badge === 'AI' ?
                          'bg-gradient-to-r from-blue-500 to-purple-600 text-white' :
                          item.badge === 'NEW' ?
                          'bg-gradient-to-r from-green-500 to-blue-500 text-white' :
                          ''}`
                          } data-id="np136lkfj" data-path="src/components/layout/Sidebar.tsx">

                                {item.badge}
                              </Badge>
                        }
                          </Button>
                        </motion.div>);

                })}
                  </div>
                </ScrollArea>

                {/* Bottom Section - AI Assistant or Upgrade Prompt */}
                {!isSuperAdmin &&
            <div className="mt-4 pt-4 border-t border-gray-200" data-id="mvm6esu9g" data-path="src/components/layout/Sidebar.tsx">
                    {hasAIAccess('basic') ?
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 rounded-lg" data-id="sf2v7qxl1" data-path="src/components/layout/Sidebar.tsx">
                        <div className="flex items-center space-x-2 mb-2" data-id="qda7cxyp2" data-path="src/components/layout/Sidebar.tsx">
                          <MessageCircle className="h-4 w-4 text-blue-600" data-id="3mxo2mwq5" data-path="src/components/layout/Sidebar.tsx" />
                          <span className="text-sm font-medium text-blue-900" data-id="7hx72jtfj" data-path="src/components/layout/Sidebar.tsx">AI Assistant</span>
                        </div>
                        <p className="text-xs text-blue-700 mb-3" data-id="nnxp03ic7" data-path="src/components/layout/Sidebar.tsx">
                          {userTier?.tier_level === 2 ?
                  'Get instant help with all AI features!' :
                  'Chat with AI assistant (Basic tier)'
                  }
                        </p>
                        <Button
                  size="sm"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => handleMobileMenuItemClick('chat')} data-id="r08z1c1e5" data-path="src/components/layout/Sidebar.tsx">

                          <MessageCircle className="h-3 w-3 mr-1" data-id="ekiib58hi" data-path="src/components/layout/Sidebar.tsx" />
                          Chat Now
                        </Button>
                      </div> :

              userTier?.tier_level === 1 &&
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200" data-id="e553kulnt" data-path="src/components/layout/Sidebar.tsx">
                          <div className="flex items-center space-x-2 mb-2" data-id="pmpba6szy" data-path="src/components/layout/Sidebar.tsx">
                            <Crown className="h-4 w-4 text-purple-600" data-id="etbbi9jqd" data-path="src/components/layout/Sidebar.tsx" />
                            <span className="text-sm font-medium text-purple-900" data-id="y2khy2yue" data-path="src/components/layout/Sidebar.tsx">Upgrade to Premium</span>
                          </div>
                          <p className="text-xs text-purple-700 mb-3" data-id="ssuzvlocw" data-path="src/components/layout/Sidebar.tsx">
                            Unlock advanced AI features like lesson plans, presentation generator, quiz generator, and plagiarism checker.
                          </p>
                          <div className="text-xs text-purple-600" data-id="k728pevq8" data-path="src/components/layout/Sidebar.tsx">
                            Contact your administrator to upgrade.
                          </div>
                        </div>

              }
                  </div>
            }
              </div>
            </motion.div>
          </>
      }
      </AnimatePresence>
    </>;


  // Desktop Sidebar Component
  const DesktopSidebar = () =>
  <motion.aside
    initial={{ x: -100, opacity: 0 }}
    animate={{
      x: 0,
      opacity: 1,
      width: isCollapsed ? '80px' : '320px'
    }}
    transition={{ duration: 0.3 }}
    className={`${isCollapsed ? 'w-20' : 'w-80'} bg-white border-r border-gray-200 h-full relative`} data-id="xqvskb2kn" data-path="src/components/layout/Sidebar.tsx">

      {/* Toggle Button */}
      <Button
      variant="ghost"
      size="sm"
      onClick={() => setIsCollapsed(!isCollapsed)}
      className="absolute -right-3 top-4 z-10 h-6 w-6 rounded-full border bg-white shadow-md hover:bg-gray-50" data-id="mqkn301kl" data-path="src/components/layout/Sidebar.tsx">

        {isCollapsed ?
      <ChevronRight className="h-3 w-3" data-id="ify6h2m3c" data-path="src/components/layout/Sidebar.tsx" /> :

      <ChevronLeft className="h-3 w-3" data-id="cqq9tyhmq" data-path="src/components/layout/Sidebar.tsx" />
      }
      </Button>

      <ScrollArea className="h-full py-6" data-id="tk5iwugni" data-path="src/components/layout/Sidebar.tsx">
        {/* User Tier Badge for Teachers */}
        {!isSuperAdmin && userTier && !isCollapsed &&
      <div className="mx-4 mb-4" data-id="2wpzlhkd0" data-path="src/components/layout/Sidebar.tsx">
            <div className={`p-3 rounded-lg border ${
        userTier.tier_level === 2 ?
        'bg-purple-50 border-purple-200' :
        'bg-green-50 border-green-200'}`
        } data-id="3o5tr92f8" data-path="src/components/layout/Sidebar.tsx">
              <div className="flex items-center gap-2" data-id="6xwqckqlo" data-path="src/components/layout/Sidebar.tsx">
                {userTier.tier_level === 2 ?
            <Crown className="w-4 h-4 text-purple-600" data-id="78r15zlg0" data-path="src/components/layout/Sidebar.tsx" /> :

            <Shield className="w-4 h-4 text-green-600" data-id="xwb25c1p0" data-path="src/components/layout/Sidebar.tsx" />
            }
                <span className={`text-sm font-medium ${
            userTier.tier_level === 2 ? 'text-purple-700' : 'text-green-700'}`
            } data-id="ps8eimb8f" data-path="src/components/layout/Sidebar.tsx">
                  {userTier.tier_name} Tier
                </span>
              </div>
              <p className={`text-xs mt-1 ${
          userTier.tier_level === 2 ? 'text-purple-600' : 'text-green-600'}`
          } data-id="cpv1w4ymm" data-path="src/components/layout/Sidebar.tsx">
                {userTier.tier_level === 2 ?
            'Full AI access enabled' :
            'Basic AI access only'
            }
              </p>
            </div>
            
            {/* Admin Badge */}
            {isAdmin && !isSuperAdmin &&
        <div className="mt-2 p-2 rounded-lg bg-blue-50 border border-blue-200" data-id="jn2a0iva9" data-path="src/components/layout/Sidebar.tsx">
                <div className="flex items-center gap-2" data-id="m1kau2coo" data-path="src/components/layout/Sidebar.tsx">
                  <Shield className="w-4 h-4 text-blue-600" data-id="ikc3n2v1o" data-path="src/components/layout/Sidebar.tsx" />
                  <span className="text-sm font-medium text-blue-700" data-id="i72ygzzm5" data-path="src/components/layout/Sidebar.tsx">Admin Access</span>
                </div>
              </div>
        }
          </div>
      }
        
        <div className="px-4 space-y-2" data-id="uysf37j98" data-path="src/components/layout/Sidebar.tsx">
          {menuItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          // Admin users should have access to all features, including Questions Pool
          const hasAccess = !item.requiresAccess || hasAIAccess(item.requiresAccess) || isSuperAdmin || isAdmin;

          console.log(`Sidebar - Desktop item ${item.id}: hasAccess=${hasAccess}, requiresAccess=${item.requiresAccess}, isSuperAdmin=${isSuperAdmin}, isAdmin=${isAdmin}`);

          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }} data-id="mjd5ldbjt" data-path="src/components/layout/Sidebar.tsx">

                <Button
                variant={isActive ? 'default' : 'ghost'}
                className={`w-full ${isCollapsed ? 'justify-center' : 'justify-start'} text-left space-x-3 ${
                isActive ?
                'bg-gradient-to-r from-blue-500 to-purple-600 text-white' :
                hasAccess ?
                'hover:bg-gray-100' :
                'text-gray-400 cursor-not-allowed hover:bg-transparent'}`
                }
                onClick={() => hasAccess && handleDesktopMenuItemClick(item.id)}
                disabled={!hasAccess}
                title={isCollapsed ? item.label : undefined} data-id="k7yenwkd3" data-path="src/components/layout/Sidebar.tsx">

                  <Icon className="h-4 w-4" data-id="uc3fc97gf" data-path="src/components/layout/Sidebar.tsx" />
                  {!isCollapsed && <span className="flex-1" data-id="nmu8qau9v" data-path="src/components/layout/Sidebar.tsx">{item.label}</span>}
                  
                  {/* Access restriction indicator */}
                  {!hasAccess && item.requiresAccess && !isCollapsed &&
                <Badge
                  variant="outline"
                  className="text-xs bg-gray-100 text-gray-500 border-gray-300" data-id="oqzyze4jx" data-path="src/components/layout/Sidebar.tsx">

                      Tier 2
                    </Badge>
                }
                  
                  {/* Regular badges */}
                  {item.badge && hasAccess && !isCollapsed &&
                <Badge
                  variant={item.badge === 'AI' ? 'secondary' : 'default'}
                  className={`text-xs ${
                  item.badge === 'ADMIN' ?
                  'bg-red-500 text-white' :
                  item.badge === 'AI' ?
                  'bg-gradient-to-r from-blue-500 to-purple-600 text-white' :
                  item.badge === 'NEW' ?
                  'bg-gradient-to-r from-green-500 to-blue-500 text-white' :
                  ''}`
                  } data-id="nm9we9gif" data-path="src/components/layout/Sidebar.tsx">

                      {item.badge}
                    </Badge>
                }
                </Button>
              </motion.div>);

        })}
        </div>

        {/* AI Assistant Quick Access for Teachers with basic tier */}
        {!isSuperAdmin && hasAIAccess('basic') && !isCollapsed &&
      <div className="px-4 mt-8" data-id="qkrjveoak" data-path="src/components/layout/Sidebar.tsx">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-4 rounded-lg" data-id="5gwbgjd48" data-path="src/components/layout/Sidebar.tsx">
              <div className="flex items-center space-x-2 mb-2" data-id="53mh5ob35" data-path="src/components/layout/Sidebar.tsx">
                <MessageCircle className="h-4 w-4 text-blue-600" data-id="99c93t3fh" data-path="src/components/layout/Sidebar.tsx" />
                <span className="text-sm font-medium text-blue-900" data-id="k989xejpf" data-path="src/components/layout/Sidebar.tsx">AI Assistant</span>
              </div>
              <p className="text-xs text-blue-700 mb-3" data-id="yi4bgxuh9" data-path="src/components/layout/Sidebar.tsx">
                {userTier?.tier_level === 2 ?
            'Get instant help with all AI features!' :
            'Chat with AI assistant (Basic tier)'
            }
              </p>
              <Button
            size="sm"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => handleDesktopMenuItemClick('chat')} data-id="wrjao6vgm" data-path="src/components/layout/Sidebar.tsx">

                <MessageCircle className="h-3 w-3 mr-1" data-id="vqqkdatac" data-path="src/components/layout/Sidebar.tsx" />
                Chat Now
              </Button>
            </div>
          </div>
      }
        
        {/* Upgrade prompt for Tier 1 users */}
        {!isSuperAdmin && userTier?.tier_level === 1 && !isCollapsed &&
      <div className="px-4 mt-6" data-id="o5ygrnv2x" data-path="src/components/layout/Sidebar.tsx">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200" data-id="ju9gtbccx" data-path="src/components/layout/Sidebar.tsx">
              <div className="flex items-center space-x-2 mb-2" data-id="2mpqosh8e" data-path="src/components/layout/Sidebar.tsx">
                <Crown className="h-4 w-4 text-purple-600" data-id="mova38701" data-path="src/components/layout/Sidebar.tsx" />
                <span className="text-sm font-medium text-purple-900" data-id="a1v4qluom" data-path="src/components/layout/Sidebar.tsx">Upgrade to Premium</span>
              </div>
              <p className="text-xs text-purple-700 mb-3" data-id="zku5uwapc" data-path="src/components/layout/Sidebar.tsx">
                Unlock advanced AI features like lesson plans, presentation generator, quiz generator, and plagiarism checker.
              </p>
              <div className="text-xs text-purple-600" data-id="nmqg5o2cr" data-path="src/components/layout/Sidebar.tsx">
                Contact your administrator to upgrade.
              </div>
            </div>
          </div>
      }
      </ScrollArea>
    </motion.aside>;


  // Return appropriate component based on device type
  return isMobile ? <MobileFloatingMenu data-id="hc7x9y94f" data-path="src/components/layout/Sidebar.tsx" /> : <DesktopSidebar data-id="js2y58aa2" data-path="src/components/layout/Sidebar.tsx" />;
};

export default Sidebar;